import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
    Colours,
    Sizes,
    Typography,
    PrimaryAnchor,
    PageBehaviourButton,
    IconPlus,
    IconMinus,
    Caption,
    AnimatedExpandingContainer,
    MessagePropType,
    PillCaption,
} from '@ratehub/base-ui';


function SavedDocumentsRowInsurance({
    pillMessage,
    href,
    hrefMessage,
    SummaryComponent,
    details,
    'data-name': dataName,
    isCompleted,
    children,
    ...otherProps
}) {
    const [ isExpanded, setIsExpanded ] = useState(false);

    function handleToggleIsExpanded() {
        setIsExpanded(!isExpanded);
    }

    return (
        <Container
            isCompleted={isCompleted}
            {...otherProps}
        >
            <If condition={pillMessage}>
                <PillCaption
                    className="pill"
                    backgroundColour={Colours.YUZU}
                    message={pillMessage}
                />
            </If>

            {/* Since children are rendered below, we have to accept
                a component for this spot. This part of the history row
                deviates the most between BUs and can be updated to
                be more generic if applicable later. */}
            {SummaryComponent}

            <div className="flex-container">
                <div className="details-container">
                    <For
                        each="detail"
                        of={details}
                    >
                        <div
                            key={`detail-${detail.label.id}`}
                            className="details"
                        >
                            <Caption
                                forwardedAs="span"
                                className="details-label"
                                message={detail.label}
                            />
                            <Caption
                                forwardedAs="span"
                                className="details-value"
                                message={detail.value}
                            />
                        </div>
                    </For>
                </div>

                <div className="button-container">
                    <Choose>
                        <When condition={children}>
                            <PageBehaviourButton
                                onClick={handleToggleIsExpanded}
                                iconComponent={isExpanded ? IconMinus : IconPlus}
                                iconLocation="left"
                                size="small"
                                data-name="Dashboard-expandQuoteDetails"
                                message={(
                                    <FormattedMessage
                                        id="Dashboard.SavedDocumentsRowInsurance.expandDetails"
                                        defaultMessage="{
                                            isExpanded, select,
                                                true {Hide quote details}
                                                other {View quote details}
                                        }"
                                        values={{ isExpanded }}
                                    />
                                )}
                            />
                        </When>
                        <When condition={href && hrefMessage}>
                            <PrimaryAnchor
                                href={href}
                                size="medium"
                                data-name={dataName}
                                message={hrefMessage}
                            />
                        </When>
                    </Choose>
                </div>
            </div>

            <If condition={children}>
                <AnimatedExpandingContainer
                    isVisible={isExpanded}
                    data-test-name="saved-documents-row-expand-container"
                >
                    {children}
                </AnimatedExpandingContainer>
            </If>
        </Container>
    );
}

SavedDocumentsRowInsurance.propTypes = {
    pillMessage: PropTypes.oneOfType([
        MessagePropType,
        PropTypes.bool,
    ]),
    href: PropTypes.string,
    hrefMessage: MessagePropType,
    SummaryComponent: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.node,
    ]).isRequired,
    details: PropTypes.arrayOf(PropTypes.shape({
        label: MessagePropType,
        value: MessagePropType,
    })).isRequired,
    children: PropTypes.node,
    'data-name': PropTypes.string,
    isCompleted: PropTypes.bool.isRequired,
};

SavedDocumentsRowInsurance.defaultProps = {
    pillMessage: undefined,
    href: undefined,
    hrefMessage: undefined,
    children: undefined,
    'data-name': 'Dashboard-resume',
};


const Container = styled.div`
    position: relative;

    ${props => !props.isCompleted && `
        margin-top: ${Sizes.SPACING.ONE};
        padding: ${Sizes.SPACING.ONE_AND_A_HALF} 0 ${Sizes.SPACING.HALF} 0;
    `}
    ${props => props.isCompleted && `
        margin-top: ${Sizes.SPACING.ONE};
        padding: 0 0 ${Sizes.SPACING.HALF} 0;
    `}

    list-style: none;

    > .pill {
        position: absolute;
        top: 0;
        left: 0;

        width: fit-content;
        margin: 0;

        font-size: ${Sizes.FONTS['2XS']};

        strong {
            font-weight: ${Typography.WEIGHTS.MEDIUM};
        }
    }

    > .flex-container {
        ${props => !props.isCompleted && `
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            align-content: center;
        `}

        > .details-container {
            flex: 60% 1 0;

            text-transform: uppercase;

            > .details {
                margin: 0 0 ${Sizes.SPACING.THREE_QUARTERS} 0;

                > .details-label {
                    margin-right: ${Sizes.SPACING.QUARTER};
                    color: ${Colours.STONE_DARKEST};
                }

                > .details-label,
                > .details-value {
                    font-weight: ${Typography.WEIGHTS.MEDIUM};
                }
            }
        }

        > .button-container {
            flex: 185px 1 0;

            display: flex;
            ${props => !props.isCompleted && `
                justify-content: flex-end;
            `}
            ${props => props.isCompleted && `
                margin-top: ${Sizes.SPACING.ONE_AND_A_HALF};
                justify-content: flex-start;
            `}
        }
    }
`;

export default SavedDocumentsRowInsurance;
